/*
  File: src/components/home/EventsScroller.jsx
  Purpose: Futuristic horizontally scrollable event carousel with neon/glass styling.
  Props:
  - files: string[] of image filenames under public/events OR objects { id, title, thumb, status }
  - onSelect(item): click handler when a card is pressed
  - formatLabel(item): function mapping item to human label
*/
import React, { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faPlay } from '@fortawesome/free-solid-svg-icons';

/**
 * EventsScroller
 * Horizontal scroll of event image cards.
 */
function EventsScroller({ files, onSelect, formatLabel }) {
    const scrollerRef = useRef(null);
    const scrollByAmount = (dir) => {
        const el = scrollerRef.current;
        if (!el) return;
        const amount = Math.floor(el.clientWidth * 0.9) * dir;
        el.scrollBy({ left: amount, behavior: 'smooth' });
    };
    return (
        <section className="relative z-10 -mt-12 mb-10 sm:mb-14">
            {/* Ambient neon glow background */}
            <div className="pointer-events-none absolute inset-0 -z-10">
                <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-[70%] h-48 bg-gradient-to-r from-cyan-500/20 via-fuchsia-500/10 to-blue-500/20 blur-3xl rounded-full" />
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md shadow-[0_0_40px_-10px_rgba(56,189,248,0.35)] overflow-hidden pb-6 sm:pb-8">
                    <div className="flex items-center justify-between px-4 pt-4">
                        <h3 className="text-[11px] tracking-[0.25em] uppercase text-cyan-300/90 drop-shadow">Upcoming Events</h3>
                        <div className="hidden sm:flex items-center gap-2">
                            <button
                                aria-label="Previous"
                                onClick={() => scrollByAmount(-1)}
                                className="w-10 h-10 rounded-full border border-cyan-400/30 text-cyan-200/90 bg-black/40 hover:bg-cyan-400/10 hover:shadow-[0_0_24px_rgba(34,211,238,0.5)] transition-all duration-200"
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </button>
                            <button
                                aria-label="Next"
                                onClick={() => scrollByAmount(1)}
                                className="w-10 h-10 rounded-full border border-fuchsia-400/30 text-fuchsia-200/90 bg-black/40 hover:bg-fuchsia-400/10 hover:shadow-[0_0_24px_rgba(217,70,239,0.5)] transition-all duration-200"
                            >
                                <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                        </div>
                    </div>
                    {/* Edge gradients removed to avoid dark artifacts at card edges */}
                    <div
                        ref={scrollerRef}
                        className="mt-2 flex gap-5 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-5 px-4"
                        style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                        {files.map((file) => (
                            <div
                                key={typeof file === 'string' ? file : (file.id || formatLabel(file))}
                                data-item-id={typeof file === 'string' ? file : file.id}
                                className="group relative shrink-0 w-[78vw] sm:w-[360px] md:w-[440px] rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-md snap-start shadow-[0_8px_30px_rgba(0,0,0,0.4)] hover:shadow-[0_0_40px_rgba(56,189,248,0.35)] transition-all duration-300"
                            >
                                <div className="relative w-full aspect-[16/9]">
                                    {typeof file === 'string' ? (
                                        <img
                                            src={`${process.env.PUBLIC_URL}/events/${file}`}
                                            alt={formatLabel(file)}
                                            className="absolute inset-0 w-full h-full object-cover transform transition-transform duration-500 ease-out group-hover:scale-[1.06]"
                                            onError={(e) => { e.currentTarget.replaceWith(document.createTextNode(formatLabel(file))); }}
                                        />
                                    ) : (
                                        <img
                                            src={String(file.thumb || '').startsWith('http') ? file.thumb : (file.thumb || `${process.env.PUBLIC_URL}/events/placeholder.jpg`)}
                                            alt={formatLabel(file)}
                                            className="absolute inset-0 w-full h-full object-cover transform transition-transform duration-500 ease-out group-hover:scale-[1.06]"
                                            onError={(e) => { e.currentTarget.replaceWith(document.createTextNode(formatLabel(file))); }}
                                        />
                                    )}
                                    {/* Subtle overlay to improve text readability without dark edges */}
                                    <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-black/10 to-transparent" />
                                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <div className="absolute -inset-1 rounded-2xl bg-[conic-gradient(from_180deg_at_50%_50%,rgba(34,211,238,0.18)_0deg,rgba(217,70,239,0.18)_180deg,rgba(34,211,238,0.18)_360deg)] blur-md" />
                                    </div>
                                    {/* Bottom content */}
                                    <div className="absolute bottom-2 left-2 right-2 flex items-end justify-between gap-2">
                                        <div className="text-left">
                                            <div className="text-sm font-semibold leading-tight line-clamp-2 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.6)]">{formatLabel(file)}</div>
                                            <div className="flex items-center gap-2 text-[10px]">
                                                {(() => {
                                                    const status = (typeof file === 'object' && file.status) ? file.status : 'Upcoming';
                                                    const statusLower = status.toLowerCase();
                                                    let statusClass = 'text-cyan-200/80';
                                                    let statusIcon = '';
                                                    
                                                    if (statusLower === 'live') {
                                                        statusClass = 'text-red-400 bg-red-900/20 border-red-500/30 px-2 py-0.5 rounded-full border animate-pulse';
                                                        statusIcon = '🔴';
                                                    } else if (statusLower === 'not started' || statusLower === 'scheduled') {
                                                        statusClass = 'text-gray-400 bg-gray-900/20 border-gray-500/30 px-2 py-0.5 rounded-full border';
                                                        statusIcon = '⚫';
                                                    } else if (statusLower === 'ended' || statusLower === 'finished') {
                                                        statusClass = 'text-green-400 bg-green-900/20 border-green-500/30 px-2 py-0.5 rounded-full border';
                                                        statusIcon = '✅';
                                                    } else {
                                                        statusClass = 'text-cyan-200/80';
                                                    }
                                                    
                                                    return (
                                                        <span className={statusClass}>
                                                            {statusIcon && <span className="mr-1">{statusIcon}</span>}
                                                            {status}
                                                        </span>
                                                    );
                                                })()}
                                                <span className="text-cyan-200/80">• 4K • HDR</span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => onSelect(file)}
                                            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-cyan-500/30 text-white border border-cyan-400/40 backdrop-blur-sm shadow-[0_0_24px_rgba(34,211,238,0.45)] group-hover:scale-105 transition-transform focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
                                            aria-label={`Play ${formatLabel(file)}`}
                                        >
                                            <FontAwesomeIcon icon={faPlay} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default EventsScroller;


